import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import { ArrowRightIcon, BookSearchOutlineIcon } from '../../../../atoms/material-icons';
import RangeSelector from '../../../../atoms/range-slider';
import EmptyState from '../empty-state';
import { isWithinTradingHours, widgetLink } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import './sectoral-indices.m.css';

const SectoralIndices = ({ widget = false, container = '' }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [sectoralIndicesData, setSectoralIndicesData] = useState([]);
  const [indicesDetails, setIndicesDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState('1D');
  const [oneD, setOneD] = useState(false);
  const [oneW, setOneW] = useState(false);
  const [oneM, setOneM] = useState(false);
  const [oneYTD, setOneYTD] = useState(false);
  const [oneY, setOneY] = useState(false);
  const [threeY, setThreeY] = useState(false);
  const [toggle, setToggle] = useState(-1);
  const [selectedKey, setSelectedKey] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );
  const [isDarkMode, setIsDarkMode] = useState(false);

  const getSectoralIndicesData = async (duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/sectoral-indices/${duration}`);
    return response.json();
  };

  const getIndicesDetailsData = async (key = '127', duration = '1D') => {
    const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
    return response.json();
  };

  const resultSectoralIndices = useQuery(
    'getSectoralIndicesData',
    () => getSectoralIndicesData(selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setSectoralIndicesData(data.data);
      },
      enabled:
        initialLoading ||
        (!!selectedDuration && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  const resultIndicesDetails = useQuery(
    'getIndicesData',
    () => getIndicesDetailsData(selectedKey, selectedDuration),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setIndicesDetails(data.data);
      },
      enabled:
        initialLoading ||
        (!!selectedKey &&
          !!selectedDuration &&
          isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const getSectoralIndicesData = async () => {
      const response = await fetch(`/feapi/markets/indices/sectoral-indices/${selectedDuration}`);
      const data = await response.json();
      setSectoralIndicesData(data.data);
    };

    const getIndicesDetailsData = async () => {
      const response = await fetch(`/feapi/markets/indices/127/details/${selectedDuration}`);
      const data = await response.json();
      setIndicesDetails(data.data);
    };

    getSectoralIndicesData();
    getIndicesDetailsData();
  }, []);

  useEffect(() => {
    setSelectedDuration('1D');
    setSelectedKey('127');

    if (resultSectoralIndices?.isSuccess) {
      setSectoralIndicesData(resultSectoralIndices?.data?.data);
    }

    if (resultIndicesDetails?.isSuccess) {
      setIndicesDetails(resultIndicesDetails?.data?.data);
    }

    setOneD(true);
    setOneW(false);
    setOneM(false);
    setOneYTD(false);
    setOneY(false);
    setThreeY(false);
    setToggle(0);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const durationHandler = async (duration) => {
    setLoading(true);
    const response = await fetch(`/feapi/markets/indices/sectoral-indices/${duration}`);
    const data = await response.json();
    setSectoralIndicesData(data.data);
    setLoading(false);
  };

  const options = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    scales: {
      x: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
      y: {
        ticks: {
          display: false,
        },
        grid: {
          drawBorder: false,
          display: false,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const requiredLabels =
    indicesDetails &&
    indicesDetails?.graph?.map((value) => {
      return value[0];
    });

  const data = {
    labels: requiredLabels,
    datasets: [
      {
        fill: true,
        label: '',
        data:
          indicesDetails &&
          indicesDetails?.graph?.map((value) => {
            return value[1];
          }),
        borderColor: '#14c588',
        backgroundColor: '#14c588',
      },
    ],
  };

  const fetchIndiceDetailsHandler = async (key, duration, index) => {
    try {
      setLoading(true);
      const response = await fetch(`/feapi/markets/indices/${key}/details/${duration}`);
      const data = await response.json();
      setIndicesDetails(data.data);
      setLoading(false);
      setToggle(index);
    } catch (error) {
      console.log('Error Fetching Indice details', error);
    }
  };

  const total = indicesDetails?.highlights?.declines + indicesDetails?.highlights?.advances;

  if (initialLoading) {
    return <div styleName="initial-loading-container"></div>;
  }

  return sectoralIndicesData?.length === 0 || isEmpty(indicesDetails) ? (
    <div styleName="no-content-outermost">
      <div styleName="header-title-container">
        <BookSearchOutlineIcon size={'26px'} color={'#d01e51'} />
        <h4 styleName="list-header-title">SECTORAL INDICES</h4>
      </div>
      <div styleName="no-data-container-ext">
        <div styleName="no-data-container-int">
          <EmptyState />
        </div>
      </div>
    </div>
  ) : (
    <div styleName={`sectoral-indices-container ${widget ? 'widget-mode' : ''}`}>
      <div styleName="sectoral-ind-left">
        <div styleName="header-title-container">
          <BookSearchOutlineIcon size={'26px'} color={'#d01e51'} />
          <h4 styleName="list-header-title">SECTORAL INDICES</h4>
        </div>
        <div styleName="btn-container">
          <button
            styleName={`filter-btn ${oneD ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('1D');
              setSelectedDuration('1D');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, '1D', 0);
              setOneD(true);
              setOneW(false);
              setOneM(false);
              setOneYTD(false);
              setOneY(false);
              setThreeY(false);
            }}
          >
            1D
          </button>
          <button
            styleName={`filter-btn ${oneW ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('1W');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, '1W', 0);
              setSelectedDuration('1W');
              setOneD(false);
              setOneW(true);
              setOneM(false);
              setOneYTD(false);
              setOneY(false);
              setThreeY(false);
            }}
          >
            1W
          </button>
          <button
            styleName={`filter-btn ${oneM ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('1M');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, '1M', 0);
              setSelectedDuration('1M');
              setOneD(false);
              setOneW(false);
              setOneM(true);
              setOneYTD(false);
              setOneY(false);
              setThreeY(false);
            }}
          >
            1M
          </button>
          <button
            styleName={`filter-btn ${oneYTD ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('YTD');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, 'YTD', 0);
              setSelectedDuration('YTD');
              setOneD(false);
              setOneW(false);
              setOneM(false);
              setOneYTD(true);
              setOneY(false);
              setThreeY(false);
            }}
          >
            YTD
          </button>
          <button
            styleName={`filter-btn ${oneY ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('1Y');
              setSelectedDuration('1Y');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, '1Y', 0);
              setOneD(false);
              setOneW(false);
              setOneM(false);
              setOneYTD(false);
              setOneY(true);
              setThreeY(false);
            }}
          >
            1Y
          </button>
          <button
            styleName={`filter-btn ${threeY ? 'selected' : ' '} `}
            onClick={() => {
              durationHandler('3Y');
              setSelectedDuration('3Y');
              fetchIndiceDetailsHandler(sectoralIndicesData && sectoralIndicesData[0].id, '3Y', 0);
              setOneD(false);
              setOneW(false);
              setOneM(false);
              setOneYTD(false);
              setOneY(false);
              setThreeY(true);
            }}
          >
            3Y
          </button>
        </div>
        {loading ? (
          <div styleName="loader-container">
            <Loader color={'#dadee1'} />
          </div>
        ) : (
          <div styleName={`${widget && container === '' ? 'stats-main-container' : ''}`}>
            {sectoralIndicesData.map((indice, index) => (
              <div
                styleName={`stats-container ${index === toggle ? 'selected-filter' : ''}`}
                key={indice.id}
                onClick={() => {
                  setSelectedKey(indice.id);
                  fetchIndiceDetailsHandler(indice.id, selectedDuration, index);
                }}
              >
                <div styleName="stats-one">
                  <p styleName="value-desc">{indice.name}</p>
                  {String(indice['price-movement'].chg).includes('-') ? (
                    <span styleName="negative-value">{indice['price-movement'].chg}</span>
                  ) : (
                    <span styleName="positive-value">+{indice['price-movement'].chg}</span>
                  )}
                </div>

                <div styleName="stats-two">
                  <p styleName="value-stat">{indice['price-movement']['current-price']}</p>
                  {String(indice['price-movement'].chgp).includes('-') ? (
                    <span styleName="negative-value">&#9660;{indice['price-movement'].chgp}%</span>
                  ) : (
                    <span styleName="positive-value">&#9650;+{indice['price-movement'].chgp}%</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {(!widget || (widget && container === 'full')) && (
          <div styleName="view-btn-container">
            <BQLink
              styleName="view-all-ind"
              href={widgetLink(`/markets/equities/sectoral-indices${widgetParam}`, widget)}
              target="_blank"
              rel="noreferrer"
            >
              View All Indices{' '}
              <ArrowRightIcon
                color={isDarkMode && widget ? '#FFFFFF' : '#2b2b2b'}
                styleName="tab-icon"
              />
            </BQLink>
          </div>
        )}
      </div>
      {loading ? (
        <div styleName="sectoral-ind-right loader-container">
          <Loader color={'#dadee1'} />
        </div>
      ) : (
        <div styleName="sectoral-ind-right">
          <div styleName="overall-stats">
            <div styleName="top-stats-container-one">
              <p styleName="top-stat-title">Top Gainer</p>
              {indicesDetails?.highlights?.topgainer?.name ? (
                <BQLink
                  href={widgetLink(
                    `/stock/${
                      indicesDetails?.highlights?.topgainer?.sid
                    }/${indicesDetails?.highlights?.topgainer?.name
                      ?.toLowerCase()
                      .trim()
                      .replaceAll(' ', '-')
                      .split('-')
                      .filter((val) => val !== '')
                      .map((val) => val.replaceAll('.', ''))
                      .join('-')}${widgetParam}`,
                    widget,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <p styleName="top-stat-name">{indicesDetails?.highlights?.topgainer?.name}</p>
                </BQLink>
              ) : (
                <p styleName="top-stat-name no-stat-name">No Gainers</p>
              )}

              {indicesDetails?.highlights?.topgainer?.chgp ? (
                <p styleName="top-stat-positive">
                  &#9650;+{indicesDetails?.highlights?.topgainer?.chgp}%
                </p>
              ) : (
                <p styleName="top-stat-positive">-</p>
              )}
            </div>
            <div styleName="top-stats-container-two">
              <p styleName="top-stat-title">Top Loser</p>

              {indicesDetails?.highlights?.toploser?.name ? (
                <BQLink
                  href={widgetLink(
                    `/stock/${
                      indicesDetails?.highlights?.toploser?.sid
                    }/${indicesDetails?.highlights?.toploser?.name
                      ?.toLowerCase()
                      .trim()
                      .replaceAll(' ', '-')
                      .split('-')
                      .filter((val) => val !== '')
                      .map((val) => val.replaceAll('.', ''))
                      .join('-')}${widgetParam}`,
                    widget,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <p styleName="top-stat-name">{indicesDetails?.highlights?.toploser?.name}</p>
                </BQLink>
              ) : (
                <p styleName="top-stat-name no-stat-name">No Losers</p>
              )}

              {indicesDetails?.highlights?.toploser?.chgp ? (
                <p styleName="top-stat-negative">
                  &#9660;{indicesDetails?.highlights?.toploser?.chgp}%
                </p>
              ) : (
                <p styleName="top-stat-negative">-</p>
              )}
            </div>
          </div>

          <div styleName="range-slider-container">
            <RangeSelector
              min={indicesDetails['high-low'] && indicesDetails['high-low']?.low}
              max={indicesDetails['high-low'] && indicesDetails['high-low']?.high}
              currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
              label=""
            />

            <RangeSelector
              min={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-low']}
              max={indicesDetails['high-low'] && indicesDetails['high-low']['52-week-high']}
              currentValue={indicesDetails && indicesDetails['high-low']?.currentprice}
              label=""
              labelOne="52 Week Low"
              labelTwo="52 Week High"
            />
          </div>
          <div styleName="graph-container">
            {indicesDetails?.graph?.length !== 0 ? (
              <Line options={options} data={data} />
            ) : (
              <div styleName="no-data-container-ext-alt">
                <div styleName="no-data-container-int">
                  <EmptyState />
                </div>
              </div>
            )}
          </div>
          {indicesDetails?.highlights?.advances || indicesDetails?.highlights?.declines ? (
            <>
              <div styleName="adv-dec-details">
                <span>{indicesDetails?.highlights?.advances}</span>
                <span>{indicesDetails?.highlights?.declines}</span>
              </div>
              <div styleName="adv-dec-container">
                <div
                  styleName="adv-bar"
                  style={{ width: `${(indicesDetails?.highlights?.advances / total) * 100}%` }}
                />
              </div>
              <div styleName="adv-dec-titles">
                <span>Advances</span>
                <span>Declines</span>
              </div>
            </>
          ) : (
            <div styleName="no-advances-declines">
              <span>Advances/Declines data not available at the moment.</span>
            </div>
          )}

          <div styleName="bottom-stats-container">
            {indicesDetails['historical-returns'] &&
              indicesDetails['historical-returns']?.map((value, index) => (
                <div key={index} styleName="label-stat-container">
                  <span styleName="value-label">{value.label}</span>

                  {String(value.change).includes('-') ? (
                    <span styleName="value-stat-negative">-{Math.abs(value.change)}%</span>
                  ) : (
                    <span styleName="value-stat-positive">{value.change}%</span>
                  )}
                </div>
              ))}
          </div>
          <div styleName="ext-btn-container">
            <BQLink
              styleName="ext-btn"
              href={widgetLink(
                `/markets/equities/sectoral-stocks-in-indices${widgetParam}`,
                widget,
              )}
              target="_blank"
              rel="noreferrer"
            >
              Stocks in Indices
            </BQLink>
            <BQLink
              styleName="ext-btn"
              href={widgetLink(`/markets/equities/historical-returns${widgetParam}`, widget)}
              target="_blank"
              rel="noreferrer"
            >
              Historical Returns
            </BQLink>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectoralIndices;
